/**
 * @fileoverview Moves a tab the browser restored onto an older build to the
 * live one ([#6249](https://github.com/CodeinPlace/cip/issues/6249)).
 *
 * `index.html` is revalidated on every load (see the hosting headers in
 * `firebase.json`), so an ordinary load or reload always gets the live build.
 * A history load does not: Chrome restores
 * a discarded tab, and goes back or forward without the back/forward cache,
 * from the cached `index.html` without revalidating it, so the page runs the
 * build the tab last had, hours or days old. Such a load starts a new
 * document, so a reload loses nothing it still holds.
 *
 * The reload it starts is a `reload` navigation, which this check skips, so
 * it can never loop.
 */

import {BUILD_COMMIT_META_NAME, readBuildCommit} from 'buildCommitMeta';
import {reloadPage} from 'appReload';
import {readNavigationType, readWasDiscarded} from 'pageLoadReport';

/** `readBuildCommit`'s value for a page with no build stamp. */
const UNKNOWN_BUILD_COMMIT = 'unknown';

/** What {@link reloadIfRestoredOnOldBuild} found. */
export type RestoredBuildOutcome =
  /** The load was not a history restore, so it already got the live build. */
  | 'not-restored'
  /** The running page has no build stamp (tests, a dev page). */
  | 'unknown-build'
  /** The live page could not be fetched or carried no stamp. */
  | 'live-build-unknown'
  /** The restored page already runs the live build. */
  | 'current'
  /** The restored page ran an older build; a reload was started. */
  | 'reloaded';

/**
 * Whether a page load came from the browser's history rather than the
 * network, and so may run an older build than the live one.
 *
 * @param params.navigationType - The Navigation Timing type of the load.
 * @param params.wasDiscarded - `document.wasDiscarded`, or null where the
 *   browser does not have it.
 * @return True for a `back_forward` load or a discard restore.
 * @throws Never.
 */
export function isHistoryRestore({
  navigationType,
  wasDiscarded,
}: {
  navigationType: string;
  wasDiscarded: boolean | null;
}): boolean {
  return navigationType === 'back_forward' || wasDiscarded === true;
}

/**
 * Reads the build stamp from an `index.html` document's text.
 *
 * @param html - The document's HTML.
 * @return The stamped build, or null when the document has none.
 * @throws Never.
 */
export function readBuildCommitFromHtml(html: string): string | null {
  const document = new DOMParser().parseFromString(html, 'text/html');
  const commit = document
    .querySelector(`meta[name="${BUILD_COMMIT_META_NAME}"]`)
    ?.getAttribute('content');
  return commit ? commit : null;
}

/**
 * Fetches the live `index.html`, bypassing the HTTP cache, and reads its
 * build stamp.
 *
 * @return The live build, or null when the fetch fails or the page carries
 *   no stamp.
 * @throws Never; a failed fetch is logged at debug level.
 */
export async function fetchLiveBuildCommit(): Promise<string | null> {
  try {
    const response = await fetch('/', {cache: 'no-store'});
    if (!response.ok) {
      return null;
    }
    return readBuildCommitFromHtml(await response.text());
  } catch (e) {
    console.debug('[restored-build] fetching the live page failed', e);
    return null;
  }
}

/**
 * Reloads the page when the browser restored it from history onto a build
 * other than the live one.
 *
 * @param params.navigationType - The Navigation Timing type of this load.
 * @param params.wasDiscarded - `document.wasDiscarded`, or null where the
 *   browser does not have it.
 * @param params.runningBuild - The build this page runs.
 * @param params.fetchLiveBuild - Reads the live build. Omitted → fetches `/`
 *   past the HTTP cache and reads its stamp.
 * @param params.reload - Starts the reload. Omitted → `reloadPage` with the
 *   `restored-old-build` reason.
 * @return What the check found.
 * @throws Never.
 */
export async function reloadIfRestoredOnOldBuild({
  navigationType,
  wasDiscarded,
  runningBuild,
  fetchLiveBuild = fetchLiveBuildCommit,
  reload = () => {
    reloadPage({reason: 'restored-old-build'});
  },
}: {
  navigationType: string;
  wasDiscarded: boolean | null;
  runningBuild: string;
  fetchLiveBuild?: () => Promise<string | null>;
  reload?: () => void;
}): Promise<RestoredBuildOutcome> {
  if (!isHistoryRestore({navigationType, wasDiscarded})) {
    return 'not-restored';
  }
  if (runningBuild === UNKNOWN_BUILD_COMMIT) {
    return 'unknown-build';
  }
  const liveBuild = await fetchLiveBuild();
  if (liveBuild === null) {
    return 'live-build-unknown';
  }
  if (liveBuild === runningBuild) {
    return 'current';
  }
  reload();
  return 'reloaded';
}

/**
 * Runs {@link reloadIfRestoredOnOldBuild} for this document. Call once per
 * document, after `reportPageLoad`, so the restore is recorded first.
 *
 * @return Nothing; the check finishes in the background.
 * @throws Never.
 */
export function checkRestoredBuild(): void {
  void reloadIfRestoredOnOldBuild({
    navigationType: readNavigationType(),
    wasDiscarded: readWasDiscarded(),
    runningBuild: readBuildCommit(),
  });
}
